import { create } from 'zustand';
import { getVehicleLogbook } from '@/api/generated/vehicle-logbook/vehicle-logbook';
import type {
  FacilityFlowCoreModulesFleetDtosUpdateVehicleLogbookEntryRequest,
  FacilityFlowCoreModulesFleetDtosVehicleLogbookDto,
  FacilityFlowCoreModulesFleetDtosVehicleLogbookEntryDto,
  FacilityFlowCoreModulesFleetDtosVehicleLogbookRevisionDto,
  FacilityFlowCoreModulesFleetDtosVehicleLogbookVehicleDto,
} from '@/api/generated/models';
import { FacilityFlowCoreModulesFleetDtosVehicleLogbookSort as SortColumn } from '@/api/generated/models';
import { parseAxiosError } from '@/errors/parseApiError';
import { showApiError } from '@/errors/showApiError';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';

export type VehicleLogbookDto = FacilityFlowCoreModulesFleetDtosVehicleLogbookDto;
export type VehicleLogbookVehicleDto = FacilityFlowCoreModulesFleetDtosVehicleLogbookVehicleDto;
export type VehicleLogbookEntryDto = FacilityFlowCoreModulesFleetDtosVehicleLogbookEntryDto;
export type VehicleLogbookRevisionDto = FacilityFlowCoreModulesFleetDtosVehicleLogbookRevisionDto;
export type UpdateVehicleLogbookEntryRequest = FacilityFlowCoreModulesFleetDtosUpdateVehicleLogbookEntryRequest;

/** Filter of the logbook page; the PDF uses the same one. Dates are local days (YYYY-MM-DD). */
export interface VehicleLogbookFilter {
  /** Empty = all vehicles. */
  vehicleId: string;
  from: string;
  to: string;
}

export type VehicleLogbookSort = SortColumn;

/** Column and direction of a vehicle's entry table; the server sorts before paging. */
export interface VehicleLogbookOrder {
  sort: VehicleLogbookSort;
  descending: boolean;
}

export const DEFAULT_LOGBOOK_ORDER: VehicleLogbookOrder = { sort: SortColumn.NUMBER_1, descending: false };

/** One loaded page of a vehicle's entries. */
export interface VehicleEntriesPage {
  items: VehicleLogbookEntryDto[];
  totalCount: number;
  page: number;
  order: VehicleLogbookOrder;
  loading: boolean;
  error: boolean;
}

export const LOGBOOK_PAGE_SIZE = 50;

const api = getVehicleLogbook();

/** Latest request per vehicle — a slower, older response must not overwrite a newer one. */
const latestEntriesRequest = new Map<string, number>();
let entriesRequestCounter = 0;

function periodParams(filter: VehicleLogbookFilter) {
  return { from: filter.from, to: filter.to };
}

interface VehicleLogbookState {
  /** Totals and counts per vehicle, without entries. */
  overview: VehicleLogbookDto | null;
  /** Loaded entry pages by vehicle id — reset whenever the overview is reloaded. */
  pages: Record<string, VehicleEntriesPage>;
  loading: boolean;
  saving: boolean;
  downloading: boolean;
  error: string | null;

  /** Reloads totals; drops loaded pages unless `keepPages` (after an edit the page is reloaded itself). */
  fetchOverview: (filter: VehicleLogbookFilter, keepPages?: boolean) => Promise<void>;
  /** Loads a page; without `order` the vehicle keeps its current sorting. */
  fetchEntries: (
    vehicleId: string,
    filter: VehicleLogbookFilter,
    page: number,
    order?: VehicleLogbookOrder,
  ) => Promise<void>;
  /** Saves an edit, then reloads the overview and the vehicle's current page. */
  updateEntry: (
    entry: VehicleLogbookEntryDto,
    data: UpdateVehicleLogbookEntryRequest,
    filter: VehicleLogbookFilter,
  ) => Promise<boolean>;
  downloadPdf: (filter: VehicleLogbookFilter, includePassengers: boolean) => Promise<void>;
}

/** Vehicle logbook of the administration (FAFD-26): overview first, entries page by page. */
export const useVehicleLogbookStore = create<VehicleLogbookState>((set, get) => ({
  overview: null,
  pages: {},
  loading: false,
  saving: false,
  downloading: false,
  error: null,

  fetchOverview: async (filter, keepPages = false) => {
    set((s) => ({ loading: true, error: null, pages: keepPages ? s.pages : {} }));
    try {
      const overview = await api.getApiV1VehiclesLogbook({
        ...periodParams(filter),
        ...(filter.vehicleId ? { vehicleId: filter.vehicleId } : {}),
      });
      set({ overview, loading: false });
    } catch (err) {
      // The page checks the period too; this covers a limit the API sets differently.
      const invalidPeriod = parseAxiosError(err).code === 'fleet.logbook.invalidPeriod';
      set({
        error: invalidPeriod ? 'vehicles:logbook.invalidPeriod' : 'vehicles:logbook.errors.fetchFailed',
        loading: false,
      });
    }
  },

  fetchEntries: async (vehicleId, filter, page, order) => {
    const previous = get().pages[vehicleId];
    const nextOrder = order ?? previous?.order ?? DEFAULT_LOGBOOK_ORDER;
    const requestId = ++entriesRequestCounter;
    latestEntriesRequest.set(vehicleId, requestId);
    const isLatest = () => latestEntriesRequest.get(vehicleId) === requestId;
    set((s) => ({
      pages: {
        ...s.pages,
        [vehicleId]: {
          items: previous?.items ?? [],
          totalCount: previous?.totalCount ?? 0,
          page,
          order: nextOrder,
          loading: true,
          error: false,
        },
      },
    }));
    try {
      const result = await api.getApiV1VehiclesLogbookVehicleIdEntries(vehicleId, {
        ...periodParams(filter),
        page,
        pageSize: LOGBOOK_PAGE_SIZE,
        sort: nextOrder.sort,
        descending: nextOrder.descending,
      });
      if (!isLatest()) return;
      set((s) => ({
        pages: {
          ...s.pages,
          [vehicleId]: {
            items: result.items ?? [],
            totalCount: result.totalCount ?? 0,
            page: result.page ?? page,
            order: nextOrder,
            loading: false,
            error: false,
          },
        },
      }));
    } catch {
      if (!isLatest()) return;
      set((s) => ({ pages: { ...s.pages, [vehicleId]: { ...s.pages[vehicleId], loading: false, error: true } } }));
    }
  },

  updateEntry: async (entry, data, filter) => {
    if (!entry.id || !entry.vehicleId) return false;
    const vehicleId = entry.vehicleId;
    const page = get().pages[vehicleId]?.page ?? 1;
    set({ saving: true });
    try {
      await api.putApiV1VehiclesLogbookEntryId(entry.id, data);
      set({ saving: false });
      useToastStore.getState().addToast(i18n.t('vehicles:logbook.messages.updated'), 'success');
      await get().fetchOverview(filter, true);
      await get().fetchEntries(vehicleId, filter, page);
      return true;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return false;
    }
  },

  downloadPdf: async (filter, includePassengers) => {
    set({ downloading: true });
    try {
      const blob = await api.getApiV1VehiclesLogbookPdf({
        ...periodParams(filter),
        ...(filter.vehicleId ? { vehicleId: filter.vehicleId } : {}),
        includePassengers,
      });
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fahrtenbuch_${filter.from}_${filter.to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      useToastStore.getState().addToast(i18n.t('vehicles:logbook.errors.pdfFailed'), 'error');
    } finally {
      set({ downloading: false });
    }
  },
}));

export default useVehicleLogbookStore;
